import React, {
  ReactNode,
  useCallback,
  useMemo,
} from 'react';
import {
  componentTypes,
  MenuRadio,
  RefinementList,
  HierarchicalMenu as HierarchicalMenuComponent,
  mappers,
  ToggleRefinement,
} from '@white-nl/instantsearch-components';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import {
  useHierarchicalMenu,
  useInstantSearch,
  useMenu,
  useRefinementList,
  useToggleRefinement,
} from 'react-instantsearch';
import { Keyable } from '@white-nl/instantsearch-components/dist/types/components';
import ChevronDown from '../../../../components/ui/Icons/ChevronDown';

function Dropdown({
  children,
  name,
}: {
  name: string,
  children: ReactNode,
}) {
  return (
    <Popover className="filter-dropdown z-100 relative group data-[open]:active-dropdown">
      <PopoverButton className="dropdown-opener text-base py-4 px-4 gap-x-6 btn btn-primary">
        {name}
        <ChevronDown />
      </PopoverButton>
      <PopoverPanel
        className="absolute max-h-60 overflow-x-hidden overflow-y-auto bg-white top-full left-0 min-w-full w-auto max-w-max whitespace-nowrap p-4 lg:pr-6 shadow-md rounded mt-3 flex"
      >
        {children}
      </PopoverPanel>
    </Popover>
  );
}

function GroupFilter(
  name: string,
  additionalData: Keyable,
) {
  const { results } = useInstantSearch();
  const {
    pathAttribute,
    addTrailingSlashesToUrls,
    isGroup,
    productGroups,
    groupMapping,
    parentGroupUri,
  } = additionalData;

  const menuSettings = useMemo(() => ({
    attributes: productGroups,
    limit: 99,
    rootPath: parentGroupUri,
    separator: '/',
    classNames: {
      list: 'flex flex-wrap gap-y-2.5',
      link: 'group no-underline',
      selectedItemLink: 'selected',
      item: 'w-full relative flex items-center min-h-7.5 cursor-pointer text-base leading-normal pl-10.5',
      label: 'underline underline-offset-2 decoration-transparent hover:decoration-light-grey-2 transition-all after:transition-all after:w-7.5 after:h-7.5 after:rounded-full after:border-2 after:border-light-grey-1.5 hover:after:border-dark-shade after:bg-white after:absolute after:top-2/4 after:-translate-y-2/4 after:left-0 before:absolute before:w-4.7 before:h-4.7 before:absolute before:top-2/4 before:-translate-y-2/4 before:left-1.5 before:bg-near-black before:z-10 before:rounded-full before:opacity-0 group-[.selected]:before:opacity-100',
      count: 'hidden',
    },
    transformItems: mappers.transformers.mapLabels(groupMapping),
    hardRefresh: isGroup,
  }), [productGroups, parentGroupUri, groupMapping, isGroup]);

  const menu = useHierarchicalMenu(menuSettings);

  const customCreateURL = useCallback((value: string) => {
    if (!menuSettings.hardRefresh) {
      return menu.createURL(value);
    }

    const [attribute] = menuSettings.attributes;
    const { separator } = menuSettings;

    if (pathAttribute !== attribute) {
      return menu.createURL(value);
    }

    const url = new URL(menu.createURL(value));
    url.searchParams.delete('page');

    const slug = `/${value.split(separator).join('/')}`;

    return `${url.origin}${slug}${addTrailingSlashesToUrls ? '/' : ''}${url.search}${url.hash}`;
  }, [menu, menuSettings, pathAttribute, addTrailingSlashesToUrls]);

  return (
    <Dropdown name={name}>
      <HierarchicalMenuComponent
        results={results}
        menu={menu}
        menuSettings={menuSettings}
        createURL={customCreateURL}
        additionalData={additionalData}
      />
    </Dropdown>
  );
}

function MenuFilter({
  name,
  additionalData,
}: {
  name: string,
  additionalData: Keyable,
}) {
  const { results } = useInstantSearch();
  const menu = useMenu({
    attribute: name,
    sortBy: ['name:asc'],
    limit: 999,
  });

  const { items } = menu;

  if (items.length === 0) {
    return '';
  }

  return (
    <Dropdown name={name}>
      <MenuRadio
        results={results}
        menu={menu}
        menuSettings={{
          attribute: name,
          sortBy: ['name:asc'],
          limit: 999,
          classNames: {
            list: 'flex flex-wrap gap-y-2.5',
            item: 'w-full relative flex items-center min-h-7.5 cursor-pointer text-base leading-normal pl-10.5',
            label: 'underline underline-offset-2 decoration-transparent hover:decoration-light-grey-2 transition-all after:transition-all after:w-7.5 after:h-7.5 after:rounded-full after:border-2 after:border-light-grey-1.5 hover:after:border-dark-shade after:bg-white after:absolute after:top-2/4 after:-translate-y-2/4 after:left-0 before:absolute before:w-4.7 before:h-4.7 before:absolute before:top-2/4 before:-translate-y-2/4 before:left-1.5 before:bg-near-black before:z-10 before:rounded-full before:opacity-0 peer-checked:before:opacity-100',
            radio: 'peer absolute opacity-0 invisible',
            count: 'hidden',
          },
        }}
        additionalData={additionalData}
      />
    </Dropdown>
  );
}

function RefinementFilter({
  name,
  additionalData,
}: {
  name: string,
  additionalData: Keyable,
}) {
  const { results } = useInstantSearch();
  const refinement = useRefinementList({
    attribute: name,
    sortBy: ['name:asc'],
    limit: 999,
  });

  const { items } = refinement;

  if (items.length === 0) {
    return '';
  }

  return (
    <Dropdown name={name}>
      <RefinementList
        results={results}
        refinementList={refinement}
        refinementListSettings={{
          attribute: name,
          sortBy: ['name:asc'],
          limit: 999,
          classNames: {
            list: 'flex flex-wrap gap-y-2.5',
            label: 'group/label flex gap-x-4 items-center relative min-h-7.5 decoration-transparent transition-all cursor-pointer text-base leading-normal pl-10.5 after:transition-all after:w-7.5 after:h-7.5 after:rounded after:border-2 after:border-light-grey-1.5 hover:after:border-dark-shade after:bg-white after:flex after:justify-center after:items-center after:absolute after:top-2/4 after:-translate-y-2/4 after:left-0 has-[:checked]:after:content-check',
            labelText: 'group-hover/label:underline group-hover/label:decoration-light-grey-2 underline-offset-2',
            checkbox: 'absolute opacity-0 invisible',
            count: 'min-w-5.5 h-5.5 flex items-center justify-center rounded py0.5 px-1 bg-light-shade text-sm leading-normal hover:no-underline',
          },
        }}
        additionalData={additionalData}
      />
    </Dropdown>
  );
}

function ToggleFilter(
  name: string,
  additionalData: Keyable,
) {
  const { results } = useInstantSearch();
  const toggle = useToggleRefinement({
    attribute: name,
  });

  const { catalog } = additionalData;

  return (
    <Dropdown name={Craft.t(catalog, 'Sale')}>
      <ToggleRefinement
        results={results}
        toggleRefinement={toggle}
        toggleRefinementSettings={{
          attribute: name,
          classNames: {
            label: 'group/label flex gap-x-4 items-center relative min-h-7.5 decoration-transparent transition-all cursor-pointer text-base leading-normal pl-10.5 after:transition-all after:w-7.5 after:h-7.5 after:rounded after:border-2 after:border-light-grey-1.5 hover:after:border-dark-shade after:bg-white after:flex after:justify-center after:items-center after:absolute after:top-2/4 after:-translate-y-2/4 after:left-0 peer-checked:after:content-check',
            labelText: 'group-hover/label:underline group-hover/label:decoration-light-grey-2 underline-offset-2',
            checkbox: 'peer absolute opacity-0 invisible',
          },
          label: Craft.t(catalog, 'Show only sale products'),
        }}
        additionalData={additionalData}
      />
    </Dropdown>
  );
}

export default function ProductRefinements({
  additionalData,
}: componentTypes.CustomContainerProps) {
  const {
    title,
    productSpecTypes,
    featuredSpecTypes,
    showGroups,
    showFeaturedFilters,
    showPromotionFilter,
  } = additionalData;
  return (
    <>
      {showGroups !== undefined && (
        GroupFilter(title, additionalData)
      )}
      {showFeaturedFilters !== undefined && Object.keys(Object.keys(featuredSpecTypes).length > 0
        ? featuredSpecTypes
        : productSpecTypes)
        .map((name: string) => {
          const dataType = productSpecTypes[name];
          if ([1, 2].includes(parseInt(dataType, 10))) {
            return <MenuFilter key={name} name={name} additionalData={additionalData} />;
          }
          return <RefinementFilter key={name} name={name} additionalData={additionalData} />;
        })}
      {showPromotionFilter !== undefined && (
        ToggleFilter('onPromotion', additionalData)
      )}
    </>
  );
}
